import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SquarePen, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
// import { getAreaMaster } from "../../../../services/apiService";
import ListView from "../../shared/ListView";
import ConfirmModal from "../../shared/ConfirmModal";
import { useGetAreasQuery, useDeleteAreaMutation, useBulkDeleteAreaMutation } from "../../../../services/redux/api/components";

const AreaMaster = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);

  const [deleteArea, { isLoading: isDeletingSingle }] = useDeleteAreaMutation();
  const [bulkDeleteArea, { isLoading: isDeletingBulk }] = useBulkDeleteAreaMutation();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteMode, setDeleteMode] = useState("single");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState([]);
  const selectedIdsRef = useRef([]);
  const [allSelected, setAllSelected] = useState(false);
  const [preserveSelection, setPreserveSelection] = useState(false);

  // Filter and Sort states
  const [filterColumn, setFilterColumn] = useState("AreaName");
  const [filterValue, setFilterValue] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);

  // For mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [totalCount, setTotalCount] = useState();

  // RTK Query hook
  // const { data: areas, isLoading, error } = useGetAreasQuery({
  //   pageNumber: currentPage,
  //   pageSize: itemsPerPage,
  //   sortField,
  //   sortOrder: sortDirection,
  //   columnName: isFilterApplied ? filterColumn : null,
  //   value: isFilterApplied ? filterValue : null,
  // });

  const {
    data: areas,
    isLoading,
    error,
    refetch,
  } = useGetAreasQuery({
    pageNumber: currentPage,
    pageSize: itemsPerPage,
    sortField,
    sortOrder: sortDirection,
    ...(isFilterApplied && {
      searchTerm: filterColumn,
      searchValue: filterValue,
    }),
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const options = [
      { value: "AreaName", label: "Area Name" },
      { value: "AreaDescription", label: "Description" },
    ];
    setColumnOptions(options);
  }, []);

  const rowData = useMemo(() => {
    if (!areas?.data) return [];
    return Array.isArray(areas.data)
      ? areas.data.map(item => ({
        ...item,
        id: item.AreaId,
      }))
      : [];
  }, [areas, isFilterApplied]);

  const handleFilter = () => {
    if (!filterValue.trim()) {
      toast.warning("Please enter filter value");
      return;
    }
    setIsFilterApplied(true);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterValue("");
    setIsFilterApplied(false);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    let direction = "asc";
    if (sortField === field) {
      direction = sortDirection === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const createHeaderComponent = (field, displayName) => {
    return function CustomHeader() {
      const isSorted = sortField === field;
      const isAsc = sortDirection === "asc";

      return (
        <div
          className="cursor-pointer hover:text-brandRed flex items-center"
          onClick={() => handleSort(field)}
        >
          {displayName}
          {isSorted && (
            <span className="ml-1">
              {isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </span>
          )}
        </div>
      );
    };
  };

  useEffect(() => {
    const createColumnDefs = () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 50,
        suppressMenu: true,
      },
      {
        headerName: "Area Name",
        field: "AreaName",
        flex: 1,
        headerComponent: createHeaderComponent("AreaName", "Area Name"),
        suppressMenu: true,
      },
      {
        headerName: "Description",
        field: "AreaDescription",
        flex: 1,
        headerComponent: createHeaderComponent("AreaDescription", "Description"),
        suppressMenu: true,
      },
      {
        headerName: "Action",
        field: "action",
        headerClass: 'action-header',
        cellRenderer: (params) => (
          <div className="flex space-x-2 mt-1">
            <button
              className="edit-button"
              onClick={() => navigate(`/area-master/edit/${params.data.AreaId}`)}
              title="Edit"
              onMouseDown={(e) => e.preventDefault()}
            >
              <SquarePen color="#787474" size={18} className="mt-1" />
            </button>
            <button
              className="delete-button"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(params.data.AreaId);
                setDeleteMode("single");
                onOpen();
              }}
              title="Delete"
              onMouseDown={(e) => e.preventDefault()}
            >
              <Trash2 color="#787474" size={18} className="mt-1" />
            </button>
          </div>
        ),
        sortable: false,
      },
    ];

    setColumnDefs(createColumnDefs());
  }, []);

  const onSelectionChanged = () => {
    if (isOpen || !gridRef.current?.api) return;

    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      const totalDisplayedRows = gridRef.current.api.getDisplayedRowCount() || 0;

      selectedIdsRef.current = selectedNodes.map((node) => node.data.AreaId);
      setSelectedNames(selectedNodes.map((node) => node.data.AreaName || node.data.AreaId));

      setAllSelected(selectedNodes.length === totalDisplayedRows && totalDisplayedRows > 0);
      setDeleteMode(selectedNodes.length > 1 || allSelected ? "multi" : "single");
    } catch (error) {
      console.error("Error in onSelectionChanged:", error);
    }
  };


  
  const onOpen = () => {
    if (!gridRef.current?.api) return;

    try {
      const selectedNodes = gridRef.current.api.getSelectedNodes() || [];
      selectedIdsRef.current = selectedNodes.map((node) => node.data.AreaId);
      setSelectedNames(selectedNodes.map((node) => node.data.AreaName || node.data.AreaId));
      setPreserveSelection(true);
      setIsOpen(true);
    } catch (error) {
      console.error("Error in onOpen:", error);
    }
  };

  const onClose = () => {
    setIsOpen(false);
    setAllSelected(false);
    setPreserveSelection(false);
  };

  const handleItemsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setItemsPerPage(newPageSize);
    setCurrentPage(1); // Reset to the first page when items per page is changed
  };

  const handleDeleteClick = () => {
    const count = selectedIdsRef.current.length;
    if (count === 0) {
      toast.warning("Please select at least one area to delete");
      return;
    }

    setDeleteMode(allSelected || count > 1 ? "multi" : "single");
    setDeleteId(allSelected ? null : selectedIdsRef.current[0]);
    setIsOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteMode === "single" && deleteId) {
        const response = await deleteArea(deleteId).unwrap();
        if (response.success) {
          toast.success(response.message || "Area deleted successfully");
          refetch();
        } else {
          toast.error(response.message || "Failed to delete area");
        }
      } else {
        const idsToDelete = allSelected
          ? rowData.map((x) => x.AreaId)
          : selectedIdsRef.current;

        const response = await bulkDeleteArea({ ids: idsToDelete }).unwrap();

        if (response.success) {
          toast.success(response.message || `${idsToDelete.length} areas deleted successfully`);
          refetch();
        } else {
          toast.error(response.message || "Failed to delete areas");
        }
      }
    } catch (err) {
      toast.error(err?.data?.message || "Something went wrong");
    } finally {
      onClose();
    }
  };

  useEffect(() => {
    const totalPages = Math.ceil(areas?.totalCount / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages); // Ensure we do not go beyond available pages
    }
  }, [areas?.totalCount, currentPage, itemsPerPage]);

  // Trigger page change when currentPage or itemsPerPage changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(areas?.totalCount / itemsPerPage)) {
      setCurrentPage(newPage); // Set the page to the new page number
    }
  };

  return (
    <div className="container p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-brandRed">Area Master List</h2>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 addListTable-button"
            onClick={() => navigate("/area-master/add")}
          >
            Add
          </button>
          {selectedNames.length > 0 && (
            <button
              className="px-4 py-2 addListTable-button"
              onClick={handleDeleteClick}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <ListView
        filterColumn={filterColumn}
        setFilterColumn={setFilterColumn}
        columnOptions={columnOptions}
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        handleFilter={handleFilter}
        clearFilters={clearFilters}
        isFilterApplied={isFilterApplied}
        gridRef={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={handlePageChange} // Ensure page change is handled here
        onSelectionChanged={onSelectionChanged}
        defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
        loading={isLoading}
        handleItemsPerPageChange={handleItemsPerPageChange}
        totalCount={areas?.totalCount || 0}
      />

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message={
          allSelected
            ? `Are you sure you want to delete all ${rowData.length} areas on this page?`
            : deleteMode === "multi"
              ? `Are you sure you want to delete ${selectedIdsRef.current.length} selected areas?`
              : `Are you sure you want to delete this area: ${selectedNames[0]}?`
        }
        selectedNames={selectedNames}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="backButton"
      />
    </div>
  );
};

export default AreaMaster;
